import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clapperboard, Download, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { trackEvent } from '@/lib/analytics';
import {
  animateStatus,
  fetchAnimateConfig,
  fitPhotoToFrame,
  startAnimate,
  type AnimateConfig,
  type AnimateStartResult,
} from '@/lib/animate';
import type { AnimateMotion, AnimateOffer, AnimateStartOutcome } from '@/components/chopbox/ui/state/types';
import { useToolAvailability } from '@/lib/tool-availability';

// One job at a time; remembered on this device so a reload keeps following it.
const JOB_KEY = 'chopbox.animate.job';
const POLL_MS = 5000;

export type AnimateJob =
  | { phase: 'starting' }
  | { phase: 'making'; jobId: string; progress: number; remaining: number | null }
  | { phase: 'done'; jobId: string; url: string }
  | { phase: 'failed'; credits: number }
  | { phase: 'refused'; error: string; required?: number };

function readJob(): string | null {
  try {
    return localStorage.getItem(JOB_KEY);
  } catch {
    return null;
  }
}

function writeJob(id: string | null) {
  try {
    if (id) localStorage.setItem(JOB_KEY, id);
    else localStorage.removeItem(JOB_KEY);
  } catch {
    /* following the job after a reload is a convenience */
  }
}

/**
 * ANIMATE THIS PHOTO for a page that hosts ChopBox. `offer` goes to ChopBox
 * (null until the server says the tool runs, so nothing shows before that);
 * `job` drives <AnimateStatus>. Signed out, ChopBox asks to sign up before
 * `start` is ever called.
 */
export function useAnimate({ signedIn, onCredits }: { signedIn: boolean; onCredits?: () => void }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [config, setConfig] = useState<AnimateConfig | null>(null);
  const [job, setJob] = useState<AnimateJob | null>(null);
  const jobRef = useRef<AnimateJob | null>(null);
  jobRef.current = job;
  const creditsRef = useRef(0);
  const onCreditsRef = useRef(onCredits);
  onCreditsRef.current = onCredits;

  useEffect(() => {
    let alive = true;
    void fetchAnimateConfig().then((c) => {
      if (alive) setConfig(c);
    });
    return () => {
      alive = false;
    };
  }, []);

  const follow = useCallback((jobId: string) => {
    setJob({ phase: 'making', jobId, progress: 0, remaining: null });
  }, []);

  // A job this device started before a reload.
  useEffect(() => {
    if (!signedIn) return;
    const saved = readJob();
    if (saved) follow(saved);
  }, [signedIn, follow]);

  const jobId = job && (job.phase === 'making' || job.phase === 'done') ? job.jobId : null;
  const making = job?.phase === 'making';
  useEffect(() => {
    if (!jobId || !making) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let misses = 0;
    const tick = async () => {
      const s = await animateStatus(jobId);
      if (!alive) return;
      if (s && s.status === 'completed' && s.result_url) {
        writeJob(null);
        setJob({ phase: 'done', jobId, url: s.result_url });
        trackEvent('animate_completed', { jobId });
        // The gallery lists the clip from its job row (GET /office/media), so
        // nothing is posted from here; the browser's own record used to be the
        // only copy, and its save failed for every clip (2026-10-03).
        // A toast as well as the card: the card is under the editor, out of
        // view of someone who kept editing.
        toast({ title: t('animate.done'), description: t('animate.readyBody') });
        onCreditsRef.current?.();
        return;
      }
      if (s && s.status === 'failed') {
        writeJob(null);
        const credits = s.credits_used || creditsRef.current;
        setJob({ phase: 'failed', credits });
        trackEvent('animate_failed', { jobId });
        toast({ title: t('animate.failedTitle'), description: t('animate.failed', { credits }), variant: 'destructive' });
        onCreditsRef.current?.();
        return;
      }
      if (!s) {
        // Three unreadable polls in a row: not this account's job any more
        // (signed out, another account) or gone. Stop following it.
        misses += 1;
        if (misses >= 3) {
          writeJob(null);
          setJob(null);
          return;
        }
      } else {
        misses = 0;
        setJob({ phase: 'making', jobId, progress: s.progress || 0, remaining: s.remaining_seconds });
      }
      timer = setTimeout(() => void tick(), POLL_MS);
    };
    void tick();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [jobId, making, t, toast]);

  const start = useCallback(
    async (file: File, motion: AnimateMotion): Promise<AnimateStartOutcome> => {
      if (!config) return 'refused';
      // One clip at a time, as the server rules: a second Start while one is
      // being made used to replace that job's progress here with the refusal,
      // so the first clip was no longer followed and never shown (2026-10-03).
      const current = jobRef.current;
      if (current && (current.phase === 'starting' || current.phase === 'making')) return 'busy';
      setJob({ phase: 'starting' });
      trackEvent('animate_started', { motion, credits: config.credits });
      let fitted: { blob: Blob; aspect: number };
      try {
        fitted = await fitPhotoToFrame(file, config);
      } catch {
        setJob({ phase: 'refused', error: 'photo' });
        return 'refused';
      }
      const r = await startAnimate(fitted.blob, motion, fitted.aspect);
      if (r.ok === false) {
        const refused = r as Extract<AnimateStartResult, { ok: false }>;
        setJob({ phase: 'refused', error: refused.error, ...(refused.required ? { required: refused.required } : {}) });
        return refused.error === 'render_in_progress' ? 'busy' : 'refused';
      }
      creditsRef.current = r.credits;
      writeJob(r.jobId);
      follow(r.jobId);
      onCreditsRef.current?.();
      return 'started';
    },
    [config, follow],
  );

  // While RunPod cannot run it the button stays, greyed (lib/tool-availability.ts); it comes back by itself.
  const { render: renderOnline } = useToolAvailability();
  const offer = useMemo<AnimateOffer | null>(
    () => (config?.enabled ? { credits: config.credits, seconds: config.seconds, offline: !renderOnline, start: (f, m) => (renderOnline ? start(f, m) : undefined) } : null),
    [config, start, renderOnline],
  );

  const dismiss = useCallback(() => {
    if (job?.phase === 'making') return;
    setJob(null);
  }, [job]);

  return { offer, job, dismiss, credits: config?.credits ?? 0 };
}

/** The job under the editor: starting, making (with progress), the finished video, or what went wrong. */
export function AnimateStatus({ job, credits, onDismiss }: { job: AnimateJob | null; credits: number; onDismiss: () => void }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLElement | null>(null);
  const phase = job?.phase ?? null;
  // The card sits under the editor, out of view when Start is tapped: it comes
  // into view once the clip is being made, and again when it is ready.
  useEffect(() => {
    if (phase !== 'making' && phase !== 'done') return;
    const el = ref.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [phase]);
  if (!job) return null;
  const pct = job.phase === 'making' ? Math.round(Math.min(1, Math.max(0, job.progress)) * 100) : 0;
  return (
    <section
      ref={ref}
      data-testid="animate-status"
      aria-live="polite"
      className="mb-8 rounded-2xl border border-primary/30 bg-card p-4 sm:p-5"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary" aria-hidden>
          {job.phase === 'starting' || job.phase === 'making' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clapperboard className="h-4 w-4" />}
        </span>
        <div className="min-w-0 flex-1">
          {job.phase === 'starting' && <p className="font-semibold">{t('animate.starting')}</p>}
          {job.phase === 'making' && (
            <>
              <p className="font-semibold">{pct > 0 ? t('animate.making', { pct }) : t('animate.makingNoPct')}</p>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
                <div className="h-full rounded-full bg-primary transition-[width] duration-700" style={{ width: `${Math.max(4, pct)}%` }} />
              </div>
              <p className="mt-2 text-[13px] text-muted-foreground">{t('animate.makingNote')}</p>
            </>
          )}
          {job.phase === 'done' && (
            <>
              <p className="font-semibold">{t('animate.done')}</p>
              <video src={job.url} controls playsInline loop className="mt-3 max-h-[60vh] w-full rounded-xl bg-black" />
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button asChild>
                  <a href={job.url} download="animated-photo.mp4">
                    <Download className="mr-2 h-4 w-4" aria-hidden /> {t('animate.download')}
                  </a>
                </Button>
                <span className="text-[13px] text-muted-foreground">
                  {t('animate.savedNote')}{' '}
                  <Link to="/chopshop#my-gallery" className="text-primary underline">{t('animate.gallery')}</Link>
                </span>
              </div>
            </>
          )}
          {job.phase === 'failed' && <p className="font-semibold">{t('animate.failed', { credits: job.credits || credits })}</p>}
          {job.phase === 'refused' && (
            <>
              <p className="font-semibold">
                {job.error === 'insufficient_credits'
                  ? t('animate.noCredits', { credits: job.required || credits })
                  : job.error === 'render_in_progress'
                    ? t('animate.busy')
                    : job.error === 'photo' || job.error === 'photo_type' || job.error === 'photo_too_big'
                      ? t('animate.badPhoto')
                      : t('animate.error')}
              </p>
              {job.error === 'insufficient_credits' && (
                <Button asChild variant="outline" className="mt-3">
                  <Link to="/pricing">{t('animate.getCredits')}</Link>
                </Button>
              )}
            </>
          )}
        </div>
        {job.phase !== 'making' && job.phase !== 'starting' && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label={t('animate.close')}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>
    </section>
  );
}
